import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { supabase } from "../../lib/supabase";

interface InviteData {
  project_id: string;
  project_title: string;
  contractor_name: string;
  email: string;
}

export function Onboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);

  const [formData, setFormData] = useState({
    fullName: "",
    password: "",
    confirmPassword: "",
  });

  const verifyInviteToken = useCallback(async () => {
    try {
      // Check if invite token is valid and not expired
      const { data: invite, error: inviteError } = await supabase
        .from("project_invites")
        .select(
          `
          id,
          email,
          expires_at,
          accepted_at,
          project_id,
          projects (
            id,
            title,
            contractor_id,
            users!projects_contractor_id_fkey (
              name
            )
          )
        `,
        )
        .eq("token", token)
        .single();

      if (inviteError || !invite) {
        setError("Invalid or expired invitation link");
        setLoading(false);
        return;
      }

      // Check if already accepted
      if (invite.accepted_at) {
        setError("This invitation has already been accepted");
        setLoading(false);
        return;
      }

      // Check if expired
      if (new Date(invite.expires_at) < new Date()) {
        setError("This invitation has expired");
        setLoading(false);
        return;
      }

      // Set invite data
      const project = invite.projects as any;
      setInviteData({
        project_id: project.id,
        project_title: project.title,
        contractor_name: project.users?.name || "Your contractor",
        email: invite.email,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error verifying invite:", err);
      setError("An error occurred while verifying the invitation");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    verifyInviteToken();
  }, [token, verifyInviteToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.fullName.trim()) {
      setError("Please enter your full name");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!inviteData) {
      setError("Invalid invitation data");
      return;
    }

    setSubmitting(true);

    try {
      // 1. Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: inviteData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            role: "client",
          },
        },
      });

      if (authError) {
        setError(authError.message);
        setSubmitting(false);
        return;
      }

      if (!authData.user) {
        setError("Failed to create account");
        setSubmitting(false);
        return;
      }

      // 2. Update the project with client_id
      const { error: projectError } = await supabase
        .from("projects")
        .update({ client_id: authData.user.id })
        .eq("id", inviteData.project_id);

      if (projectError) {
        console.error("Error updating project:", projectError);
        // Continue anyway - the user was created
      }

      // 3. Mark invite as accepted
      const { error: inviteError } = await supabase
        .from("project_invites")
        .update({ accepted_at: new Date().toISOString() })
        .eq("token", token);

      if (inviteError) {
        console.error("Error updating invite:", inviteError);
      }

      // 4. Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: inviteData.email,
        password: formData.password,
      });

      if (signInError) {
        setError(
          "Account created but failed to sign in. Please try logging in.",
        );
        setSubmitting(false);
        return;
      }

      // 5. Redirect to client dashboard
      navigate("/client/dashboard");
    } catch (err) {
      console.error("Error during onboarding:", err);
      setError("An unexpected error occurred. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !inviteData) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card>
            <CardContent>
              <Alert severity="error">{error}</Alert>
              <Button
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Card sx={{ width: "100%" }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <CheckCircle
                sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
              />
              <Typography variant="h4" gutterBottom>
                Welcome to BuildBridge!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You've been invited to collaborate on a project
              </Typography>
            </Box>

            {inviteData && (
              <Box sx={{ mb: 4, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {inviteData.project_title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contractor: {inviteData.contractor_name}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Email"
                  value={inviteData?.email || ""}
                  disabled
                  fullWidth
                />

                <TextField
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  required
                  fullWidth
                  disabled={submitting}
                />

                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  fullWidth
                  disabled={submitting}
                  helperText="At least 8 characters"
                />

                <TextField
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                  fullWidth
                  disabled={submitting}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creating Account...
                    </>
                  ) : (
                    "Create Account & Accept Invitation"
                  )}
                </Button>
              </Stack>
            </form>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, textAlign: "center" }}
            >
              Already have an account?{" "}
              <Button size="small" onClick={() => navigate("/login")}>
                Sign In
              </Button>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
